import React, { useState } from 'react';
import {
  Button,
  Form,
  Input,
  Select,
  DatePicker,
  message,
  Tag,
  Modal,
} from 'antd';
import { FileTextOutlined, CloseOutlined, PlusOutlined, DeleteOutlined, EditOutlined as EditIcon, SaveOutlined, WechatOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { Case, CaseStatus, updateCase, updateCaseDetail, PartyType, PartyRole, CaseParty, updateCaseParties } from '../../../services/case';
import './CaseBasicInfoMobile.css';

const { TextArea } = Input;

interface CaseBasicInfoMobileProps {
  caseData: Case;
  canEdit: boolean;
  onRefresh: () => void;
}

const CaseBasicInfoMobile: React.FC<CaseBasicInfoMobileProps> = ({ 
  caseData, 
  canEdit, 
  onRefresh 
}) => {
  // 调试：检查 wechat_group 数据
  console.log('=== 案件数据调试 ===');
  console.log('caseData:', caseData);
  console.log('wechat_group_id:', (caseData as any).wechat_group_id);
  console.log('wechat_group:', (caseData as any)?.wechat_group);
  console.log('group_id:', (caseData as any)?.wechat_group?.group_id);
  console.log('group_name:', (caseData as any)?.wechat_group?.group_name);
  
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  
  // 当事人编辑相关状态
  const [ourParties, setOurParties] = useState<CaseParty[]>([]);
  const [otherParties, setOtherParties] = useState<CaseParty[]>([]);
  const [partyModalVisible, setPartyModalVisible] = useState(false);
  const [editingParty, setEditingParty] = useState<CaseParty | null>(null);
  const [editingPartyType, setEditingPartyType] = useState<PartyType>(PartyType.OUR_SIDE);
  const [partyForm] = Form.useForm();

  // 获取状态文本
  const getStatusText = (status: CaseStatus): string => {
    const textMap: Record<CaseStatus, string> = {
      [CaseStatus.PREPARING_FILING]: '准备立案材料',
      [CaseStatus.FILED]: '立案',
      [CaseStatus.SUPPLEMENTING]: '补充材料',
      [CaseStatus.MEDIATION]: '调解',
      [CaseStatus.HEARING]: '开庭',
      [CaseStatus.AWAITING_VERDICT]: '等待裁决书',
      [CaseStatus.FIRST_INSTANCE]: '一审',
      [CaseStatus.SECOND_INSTANCE]: '二审',
      [CaseStatus.RETRIAL]: '再审',
      [CaseStatus.ENFORCEMENT]: '执行',
      [CaseStatus.ACTIVE]: '进行中',
      [CaseStatus.SUSPENDED]: '已暂停',
      [CaseStatus.MEDIATION_CLOSED]: '调解结案',
      [CaseStatus.VERDICT_CLOSED]: '裁决结案',
      [CaseStatus.CLOSED]: '已结案',
    };
    return textMap[status] || status;
  };

  // 获取当事人角色文本
  const getPartyRoleText = (role: PartyRole): string => {
    const textMap: Record<PartyRole, string> = {
      [PartyRole.PLAINTIFF]: '原告',
      [PartyRole.DEFENDANT]: '被告',
      [PartyRole.CLIENT]: '委托人',
      [PartyRole.THIRD_PARTY]: '第三人',
      [PartyRole.ENFORCEMENT_APPLICANT]: '强制执行申请人',
      [PartyRole.ENFORCEMENT_RESPONDENT]: '被申请执行人',
      [PartyRole.SUSPECT_FAMILY]: '犯罪嫌疑人家属',
      [PartyRole.SUSPECT]: '犯罪嫌疑人',
    };
    return textMap[role] || role;
  };

  // 处理编辑
  const handleEdit = () => {
    form.setFieldsValue({
      case_name: caseData.case_name,
      accept_date: caseData.accept_date && dayjs(caseData.accept_date).isValid() 
        ? dayjs(caseData.accept_date) 
        : null,
      case_level: caseData.details?.case_level,
      court_date: caseData.court_date && dayjs(caseData.court_date).isValid() 
        ? dayjs(caseData.court_date) 
        : null,
      verdict_date: caseData.verdict_date && dayjs(caseData.verdict_date).isValid() 
        ? dayjs(caseData.verdict_date) 
        : null,
      case_type: caseData.case_type,
      case_reason: caseData.details?.case_reason,
      case_status: caseData.case_status,
      court_name: caseData.details?.court_name,
      court_contact: caseData.details?.court_contact,
      case_summary: caseData.details?.case_summary,
      notes: caseData.details?.notes,
    });
    
    // 初始化当事人数据
    const our = (caseData.parties || []).filter(p => p.party_type === PartyType.OUR_SIDE);
    const other = (caseData.parties || []).filter(p => p.party_type === PartyType.OTHER_SIDE);
    setOurParties([...our]);
    setOtherParties([...other]);
    
    setEditMode(true);
  };

  // 处理取消
  const handleCancel = () => {
    form.resetFields();
    setOurParties([]);
    setOtherParties([]);
    setEditMode(false);
  };

  // 处理保存
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // 更新基本案件信息
      const updateData: any = {
        case_name: values.case_name,
        case_type: values.case_type,
        case_status: values.case_status,
        accept_date: values.accept_date ? values.accept_date.format('YYYY-MM-DD') : undefined,
        court_date: values.court_date ? values.court_date.format('YYYY-MM-DD HH:mm:ss') : undefined,
        verdict_date: values.verdict_date ? values.verdict_date.format('YYYY-MM-DD') : undefined,
      };

      await updateCase(caseData.id, updateData);

      // 更新案件详情
      const detailData = {
        case_level: values.case_level,
        case_reason: values.case_reason,
        court_name: values.court_name,
        court_contact: values.court_contact,
        case_summary: values.case_summary,
        notes: values.notes,
      };

      await updateCaseDetail(caseData.id, detailData);

      // 更新当事人信息
      const allParties = [...ourParties, ...otherParties];
      await updateCaseParties(caseData.id, allParties);

      message.success('保存成功');
      setEditMode(false);
      setOurParties([]);
      setOtherParties([]);
      onRefresh();
    } catch (error: any) {
      if (error.errorFields) {
        message.error('请检查表单填写');
      } else {
        message.error('保存失败');
      }
    } finally {
      setLoading(false);
    }
  };

  // 添加当事人
  const handleAddParty = (type: PartyType) => {
    setEditingPartyType(type);
    setEditingParty(null);
    partyForm.resetFields();
    setPartyModalVisible(true);
  };

  // 编辑当事人
  const handleEditParty = (party: CaseParty, type: PartyType) => {
    setEditingPartyType(type);
    setEditingParty(party);
    partyForm.setFieldsValue(party);
    setPartyModalVisible(true);
  };

  // 删除当事人
  const handleDeleteParty = (party: CaseParty, type: PartyType) => {
    if (type === PartyType.OUR_SIDE) {
      setOurParties(ourParties.filter(p => p !== party));
    } else {
      setOtherParties(otherParties.filter(p => p !== party));
    }
    message.success('删除成功');
  };

  // 保存当事人信息
  const handlePartyModalOk = async () => {
    try {
      const values = await partyForm.validateFields();
      const newParty: CaseParty = {
        ...values,
        party_type: editingPartyType,
      };

      if (editingParty) {
        // 更新现有当事人
        if (editingPartyType === PartyType.OUR_SIDE) {
          setOurParties(ourParties.map(p => (p === editingParty ? newParty : p)));
        } else {
          setOtherParties(otherParties.map(p => (p === editingParty ? newParty : p)));
        }
      } else {
        // 添加新当事人
        if (editingPartyType === PartyType.OUR_SIDE) {
          setOurParties([...ourParties, newParty]);
        } else {
          setOtherParties([...otherParties, newParty]);
        }
      }

      setPartyModalVisible(false);
      partyForm.resetFields();
    } catch (error) {
      // 验证失败
    }
  };

  // 快捷更新字段
  const handleQuickUpdate = async (field: string, value: any) => {
    try {
      if (field === 'accept_date' || field === 'court_date' || field === 'verdict_date') {
        const updateData: any = {
          [field]: value ? value.format(field === 'court_date' ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD') : null,
        };
        await updateCase(caseData.id, updateData);
      } else if (field === 'case_level') {
        await updateCaseDetail(caseData.id, { case_level: value });
      } else if (field === 'case_status') {
        const updateData: any = { case_status: value };
        await updateCase(caseData.id, updateData);
      }
      message.success('更新成功');
      onRefresh();
    } catch (error) {
      message.error('更新失败');
    }
  };

  // 打开企业微信群聊
  const handleOpenWeChatGroup = () => {
    const chatId = (caseData as any)?.wechat_group?.group_id;
    
    if (!chatId) {
      message.warning('该案件未关联企业微信群聊');
      return;
    }

    if (!window.wx) {
      message.error('企业微信 JS-SDK 未加载');
      return;
    }

    window.wx.invoke('openEnterpriseChat', {
      externalUserIds: [],
      groupName: '',
      chatId: chatId
    }, (res: any) => {
      if (res.err_msg !== 'openEnterpriseChat:ok') {
        console.error('打开群聊失败:', res);
        message.error('打开群聊失败');
      }
    });
  };

  // 在查看模式下，从 caseData 获取当事人；在编辑模式下，使用状态中的当事人
  const displayOurParties = editMode ? ourParties : (caseData.parties || []).filter(p => p.party_type === PartyType.OUR_SIDE);
  const displayOtherParties = editMode ? otherParties : (caseData.parties || []).filter(p => p.party_type === PartyType.OTHER_SIDE);

  return (
    <div className="case-basic-info-mobile">
      {!editMode ? (
        <>
          {/* 顶部快捷信息区 */}
          <div className="top-quick-area">
            <div className="case-title-row">
              <FileTextOutlined className="title-icon" />
              <span className="case-title-text">{caseData.case_name}</span>
            </div>

            {/* 关联群聊按钮 */}
            {(caseData as any)?.wechat_group?.group_id && (
              <div style={{ marginBottom: 12 }}>
                <Button 
                  type="primary"
                  icon={<WechatOutlined />}
                  onClick={handleOpenWeChatGroup}
                  block
                  style={{ 
                    height: 40,
                    borderRadius: 8,
                    fontSize: 15,
                    fontWeight: 500
                  }}
                >
                  打开群聊：{(caseData as any)?.wechat_group?.group_name || '未命名群聊'}
                </Button>
              </div>
            )}

            <div className="quick-field-row">
              <span className="field-label">案件承接时间：</span>
              <DatePicker
                value={caseData.accept_date && dayjs(caseData.accept_date).isValid() ? dayjs(caseData.accept_date) : null}
                onChange={(date) => handleQuickUpdate('accept_date', date)}
                bordered={false}
                placeholder="-"
                style={{ padding: 0 }}
              />
            </div>

            <div className="quick-field-row">
              <span className="field-label">审级：</span>
              <Select
                value={caseData.details?.case_level}
                onChange={(value) => handleQuickUpdate('case_level', value)}
                bordered={false}
                placeholder="-"
                style={{ flex: 1 }}
              >
                <Select.Option value="一审">一审</Select.Option>
                <Select.Option value="二审">二审</Select.Option>
                <Select.Option value="再审">再审</Select.Option>
                <Select.Option value="执行">执行</Select.Option>
              </Select>
            </div>

            <div className="quick-field-row">
              <span className="field-label">开庭时间：</span>
              <DatePicker
                showTime
                value={caseData.court_date && dayjs(caseData.court_date).isValid() ? dayjs(caseData.court_date) : null}
                onChange={(date) => handleQuickUpdate('court_date', date)}
                bordered={false}
                placeholder="-"
                format="YYYY-MM-DD HH:mm"
                style={{ padding: 0 }}
              />
            </div>

            <div className="quick-field-row">
              <span className="field-label">判决时间：</span>
              <DatePicker
                value={caseData.verdict_date && dayjs(caseData.verdict_date).isValid() ? dayjs(caseData.verdict_date) : null}
                onChange={(date) => handleQuickUpdate('verdict_date', date)}
                bordered={false}
                placeholder="-"
                style={{ padding: 0 }}
              />
            </div>
          </div>

          {/* 基础信息区 */}
          <div className="info-section">
            <div className="section-title-bar">基础</div>
            <div className="section-content">
              <div className="info-row">
                <span className="row-label">* 案件名称</span>
                <div className="row-value">{caseData.case_name}</div>
              </div>
              <div className="info-row">
                <span className="row-label">* 案件承接时间</span>
                <div className="row-value">
                  {caseData.accept_date ? dayjs(caseData.accept_date).format('YYYY-MM-DD') : '-'}
                </div>
              </div>
              <div className="info-row">
                <span className="row-label">* 负责人</span>
                <div className="row-value">{caseData.responsible_lawyer_name || '-'}</div>
              </div>
            </div>
          </div>

          {/* 我方信息区 */}
          <div className="info-section">
            <div className="section-title-bar">我方信息</div>
            <div className="section-content">
              {displayOurParties.length > 0 ? (
                displayOurParties.map((party, index) => (
                  <div key={index} className="party-detail-card">
                    <div className="party-field">
                      <span className="party-field-label">当事人角色</span>
                      <span className="party-field-value">{getPartyRoleText(party.party_role)}</span>
                    </div>
                    <div className="party-field">
                      <span className="party-field-label">姓名/名称</span>
                      <span className="party-field-value">{party.name}</span>
                    </div>
                    <div className="party-field">
                      <span className="party-field-label">性别</span>
                      <span className="party-field-value">{party.gender || '-'}</span>
                    </div>
                    <div className="party-field">
                      <span className="party-field-label">身份类型</span>
                      <span className="party-field-value">{party.identity_type || '-'}</span>
                    </div>
                    <div className="party-field">
                      <span className="party-field-label">身份证号/统一社会信用代码</span>
                      <span className="party-field-value">{party.identity_number || '-'}</span>
                    </div>
                    <div className="party-field">
                      <span className="party-field-label">联系电话</span>
                      <span className="party-field-value">{party.phone || '-'}</span>
                    </div>
                    <div className="party-field">
                      <span className="party-field-label">地址</span>
                      <span className="party-field-value">{party.address || '-'}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-hint">暂无我方当事人信息</div>
              )}
            </div>
          </div>

          {/* 对方信息区 */}
          <div className="info-section">
            <div className="section-title-bar">对方信息</div>
            <div className="section-content">
              {displayOtherParties.length > 0 ? (
                displayOtherParties.map((party, index) => (
                  <div key={index} className="party-detail-card">
                    <div className="party-field">
                      <span className="party-field-label">当事人角色</span>
                      <span className="party-field-value">{getPartyRoleText(party.party_role)}</span>
                    </div>
                    <div className="party-field">
                      <span className="party-field-label">姓名/名称</span>
                      <span className="party-field-value">{party.name}</span>
                    </div>
                    <div className="party-field">
                      <span className="party-field-label">性别</span>
                      <span className="party-field-value">{party.gender || '-'}</span>
                    </div>
                    <div className="party-field">
                      <span className="party-field-label">身份类型</span>
                      <span className="party-field-value">{party.identity_type || '-'}</span>
                    </div>
                    <div className="party-field">
                      <span className="party-field-label">身份证号/统一社会信用代码</span>
                      <span className="party-field-value">{party.identity_number || '-'}</span>
                    </div>
                    <div className="party-field">
                      <span className="party-field-label">联系电话</span>
                      <span className="party-field-value">{party.phone || '-'}</span>
                    </div>
                    <div className="party-field">
                      <span className="party-field-label">地址</span>
                      <span className="party-field-value">{party.address || '-'}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-hint">暂无对方当事人信息</div>
              )}
            </div>
          </div>

          {/* 案件信息区 */}
          <div className="info-section">
            <div className="section-title-bar">案件信息</div>
            <div className="section-content">
              <div className="info-row">
                <span className="row-label">案件名称</span>
                <div className="row-value">{caseData.case_name}</div>
              </div>
              <div className="info-row">
                <span className="row-label">案件类型</span>
                <div className="row-value">{caseData.case_type || '-'}</div>
              </div>
              <div className="info-row">
                <span className="row-label">审级</span>
                <div className="row-value">{caseData.details?.case_level || '-'}</div>
              </div>
              <div className="info-row">
                <span className="row-label">案由</span>
                <div className="row-value">{caseData.details?.case_reason || '-'}</div>
              </div>
              <div className="info-row">
                <span className="row-label">案件状态</span>
                <div className="row-value">{getStatusText(caseData.case_status)}</div>
              </div>
              <div className="info-row">
                <span className="row-label">开庭时间</span>
                <div className="row-value">
                  {caseData.court_date ? dayjs(caseData.court_date).format('YYYY-MM-DD HH:mm') : '-'}
                </div>
              </div>
              <div className="info-row">
                <span className="row-label">法院名称</span>
                <div className="row-value">{caseData.details?.court_name || '-'}</div>
              </div>
              <div className="info-row">
                <span className="row-label">法院联系方式</span>
                <div className="row-value">{caseData.details?.court_contact || '-'}</div>
              </div>
              <div className="info-row">
                <span className="row-label">负责律师</span>
                <div className="row-value">{caseData.responsible_lawyer_name || '-'}</div>
              </div>
              <div className="info-row">
                <span className="row-label">助理律师</span>
                <div className="row-value">{caseData.assistant_lawyer_name || '-'}</div>
              </div>
              <div className="info-row">
                <span className="row-label">案件描述</span>
                <div className="row-value" style={{ whiteSpace: 'pre-wrap' }}>
                  {caseData.details?.case_summary || '-'}
                </div>
              </div>
              <div className="info-row">
                <span className="row-label">备注</span>
                <div className="row-value" style={{ whiteSpace: 'pre-wrap' }}>
                  {caseData.details?.notes || '-'}
                </div>
              </div>
            </div>
          </div>

          {/* 底部编辑按钮 */}
          {canEdit && (
            <div className="fixed-bottom-single">
              <Button 
                type="primary" 
                onClick={handleEdit}
                size="large"
                block
              >
                编辑
              </Button>
            </div>
          )}
        </>
      ) : (
        <>
          {/* 编辑模式 */}
          <Form form={form} layout="vertical" className="edit-form-mobile">
            {/* 基础信息区 */}
            <div className="edit-section-block">
              <div className="section-title-bar">基础</div>
              <div className="section-content">
                <Form.Item
                  label="案件名称"
                  name="case_name"
                  rules={[{ required: true, message: '请输入案件名称' }]}
                >
                  <Input placeholder="请输入案件名称" />
                </Form.Item>

                <Form.Item
                  label="案件承接时间"
                  name="accept_date"
                  rules={[{ required: true, message: '请选择案件承接时间' }]}
                >
                  <DatePicker style={{ width: '100%' }} suffixIcon={<span>📅</span>} />
                </Form.Item>

                <Form.Item label="负责律师">
                  <div style={{ padding: '8px 0', color: '#333' }}>
                    {caseData.responsible_lawyer_name || '-'}
                  </div>
                </Form.Item>

                <Form.Item label="助理律师">
                  <div style={{ padding: '8px 0', color: '#333' }}>
                    {caseData.assistant_lawyer_name || '-'}
                  </div>
                </Form.Item>
              </div>
            </div>

            {/* 我方信息区 */}
            <div className="edit-section-block">
              <div className="section-title-bar">
                我方信息
                <Button 
                  type="link" 
                  size="small" 
                  icon={<PlusOutlined />}
                  onClick={() => handleAddParty(PartyType.OUR_SIDE)}
                  style={{ float: 'right', marginTop: -4 }}
                >
                  添加
                </Button>
              </div>
              <div className="section-content">
                {displayOurParties.length > 0 ? (
                  displayOurParties.map((party, index) => (
                    <div key={index} className="party-detail-card editable">
                      <div className="party-card-actions">
                        <Button 
                          type="link" 
                          size="small" 
                          icon={<EditIcon />}
                          onClick={() => handleEditParty(party, PartyType.OUR_SIDE)}
                        >
                          编辑
                        </Button>
                        <Button 
                          type="link" 
                          size="small" 
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteParty(party, PartyType.OUR_SIDE)}
                        >
                          删除
                        </Button>
                      </div>
                      <div className="party-field">
                        <span className="party-field-label">当事人角色</span>
                        <span className="party-field-value">{getPartyRoleText(party.party_role)}</span>
                      </div>
                      <div className="party-field">
                        <span className="party-field-label">姓名/名称</span>
                        <span className="party-field-value">{party.name}</span>
                      </div>
                      <div className="party-field">
                        <span className="party-field-label">性别</span>
                        <span className="party-field-value">{party.gender || '-'}</span>
                      </div>
                      <div className="party-field">
                        <span className="party-field-label">身份类型</span>
                        <span className="party-field-value">{party.identity_type || '-'}</span>
                      </div>
                      <div className="party-field">
                        <span className="party-field-label">身份证号/统一社会信用代码</span>
                        <span className="party-field-value">{party.identity_number || '-'}</span>
                      </div>
                      <div className="party-field">
                        <span className="party-field-label">联系电话</span>
                        <span className="party-field-value">{party.phone || '-'}</span>
                      </div>
                      <div className="party-field">
                        <span className="party-field-label">地址</span>
                        <span className="party-field-value">{party.address || '-'}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-hint">暂无我方当事人信息</div>
                )}
              </div>
            </div>

            {/* 对方信息区 */}
            <div className="edit-section-block">
              <div className="section-title-bar">
                对方信息
                <Button 
                  type="link" 
                  size="small" 
                  icon={<PlusOutlined />}
                  onClick={() => handleAddParty(PartyType.OTHER_SIDE)}
                  style={{ float: 'right', marginTop: -4 }}
                >
                  添加
                </Button>
              </div>
              <div className="section-content">
                {displayOtherParties.length > 0 ? (
                  displayOtherParties.map((party, index) => (
                    <div key={index} className="party-detail-card editable">
                      <div className="party-card-actions">
                        <Button 
                          type="link" 
                          size="small" 
                          icon={<EditIcon />}
                          onClick={() => handleEditParty(party, PartyType.OTHER_SIDE)}
                        >
                          编辑
                        </Button>
                        <Button 
                          type="link" 
                          size="small" 
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteParty(party, PartyType.OTHER_SIDE)}
                        >
                          删除
                        </Button>
                      </div>
                      <div className="party-field">
                        <span className="party-field-label">当事人角色</span>
                        <span className="party-field-value">{getPartyRoleText(party.party_role)}</span>
                      </div>
                      <div className="party-field">
                        <span className="party-field-label">姓名/名称</span>
                        <span className="party-field-value">{party.name}</span>
                      </div>
                      <div className="party-field">
                        <span className="party-field-label">性别</span>
                        <span className="party-field-value">{party.gender || '-'}</span>
                      </div>
                      <div className="party-field">
                        <span className="party-field-label">身份类型</span>
                        <span className="party-field-value">{party.identity_type || '-'}</span>
                      </div>
                      <div className="party-field">
                        <span className="party-field-label">身份证号/统一社会信用代码</span>
                        <span className="party-field-value">{party.identity_number || '-'}</span>
                      </div>
                      <div className="party-field">
                        <span className="party-field-label">联系电话</span>
                        <span className="party-field-value">{party.phone || '-'}</span>
                      </div>
                      <div className="party-field">
                        <span className="party-field-label">地址</span>
                        <span className="party-field-value">{party.address || '-'}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-hint">暂无对方当事人信息</div>
                )}
              </div>
            </div>

            {/* 案件信息区 */}
            <div className="edit-section-block">
              <div className="section-title-bar">案件信息</div>
              <div className="section-content">
                <Form.Item label="案件名称" name="case_name">
                  <Input placeholder="请输入案件名称" />
                </Form.Item>

                <Form.Item label="案件类型" name="case_type">
                  <Select placeholder="请选择案件类型">
                    <Select.Option value="民事">民事</Select.Option>
                    <Select.Option value="刑事">刑事</Select.Option>
                    <Select.Option value="行政">行政</Select.Option>
                    <Select.Option value="商事">商事</Select.Option>
                    <Select.Option value="劳动">劳动</Select.Option>
                    <Select.Option value="知识产权">知识产权</Select.Option>
                    <Select.Option value="其他">其他</Select.Option>
                  </Select>
                </Form.Item>

                <Form.Item label="审级" name="case_level">
                  <Select placeholder="请选择审级">
                    <Select.Option value="一审">一审</Select.Option>
                    <Select.Option value="二审">二审</Select.Option>
                    <Select.Option value="再审">再审</Select.Option>
                    <Select.Option value="执行">执行</Select.Option>
                  </Select>
                </Form.Item>

                <Form.Item label="案由" name="case_reason">
                  <Input placeholder="请输入案由" />
                </Form.Item>

                <Form.Item label="案件状态" name="case_status">
                  <Select placeholder="请选择案件状态">
                    <Select.Option value="filed">立案</Select.Option>
                    <Select.Option value="supplementing">补充材料</Select.Option>
                    <Select.Option value="mediation">调解</Select.Option>
                    <Select.Option value="hearing">开庭</Select.Option>
                    <Select.Option value="first_instance">一审</Select.Option>
                    <Select.Option value="second_instance">二审</Select.Option>
                    <Select.Option value="retrial">再审</Select.Option>
                    <Select.Option value="active">进行中</Select.Option>
                    <Select.Option value="suspended">已暂停</Select.Option>
                    <Select.Option value="closed">已结案</Select.Option>
                  </Select>
                </Form.Item>

                <Form.Item label="开庭时间" name="court_date">
                  <DatePicker 
                    showTime 
                    style={{ width: '100%' }} 
                    format="YYYY-MM-DD HH:mm"
                  />
                </Form.Item>

                <Form.Item label="法院名称" name="court_name">
                  <Input placeholder="请输入法院名称" />
                </Form.Item>

                <Form.Item label="法院联系方式" name="court_contact">
                  <Input placeholder="请输入法院联系方式" />
                </Form.Item>

                <Form.Item label="负责律师">
                  <div style={{ padding: '8px 0', color: '#333' }}>
                    {caseData.responsible_lawyer_name || '-'}
                  </div>
                </Form.Item>

                <Form.Item label="助理律师">
                  <div style={{ padding: '8px 0', color: '#333' }}>
                    {caseData.assistant_lawyer_name || '-'}
                  </div>
                </Form.Item>

                <Form.Item label="案件描述" name="case_summary">
                  <TextArea
                    rows={4}
                    placeholder="请输入案件描述"
                    showCount
                    maxLength={2000}
                  />
                </Form.Item>

                <Form.Item label="备注" name="notes">
                  <TextArea
                    rows={3}
                    placeholder="请输入备注"
                    showCount
                    maxLength={1000}
                  />
                </Form.Item>
              </div>
            </div>

            {/* 底部占位 */}
            <div style={{ height: 70 }}></div>
          </Form>

          {/* 底部按钮 */}
          <div className="fixed-bottom-double">
            <Button 
              size="large"
              onClick={handleCancel}
              block
              style={{ marginRight: 12 }}
            >
              取消
            </Button>
            <Button 
              type="primary"
              size="large"
              loading={loading}
              onClick={handleSave}
              block
            >
              保存
            </Button>
          </div>
        </>
      )}

      {/* 当事人编辑弹窗 */}
      <Modal
        title={editingParty ? '编辑当事人' : '添加当事人'}
        open={partyModalVisible}
        onOk={handlePartyModalOk}
        onCancel={() => {
          setPartyModalVisible(false);
          partyForm.resetFields();
        }}
        okText="确定"
        cancelText="取消"
        width="90%"
        style={{ top: 20 }}
      >
        <Form form={partyForm} layout="vertical">
          <Form.Item
            label="当事人角色"
            name="party_role"
            rules={[{ required: true, message: '请选择当事人角色' }]}
          >
            <Select placeholder="请选择当事人角色">
              <Select.Option value="plaintiff">原告</Select.Option>
              <Select.Option value="defendant">被告</Select.Option>
              <Select.Option value="client">委托人</Select.Option>
              <Select.Option value="third_party">第三人</Select.Option>
              <Select.Option value="enforcement_applicant">强制执行申请人</Select.Option>
              <Select.Option value="enforcement_respondent">被申请执行人</Select.Option>
              <Select.Option value="suspect_family">犯罪嫌疑人家属</Select.Option>
              <Select.Option value="suspect">犯罪嫌疑人</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="姓名/名称"
            name="name"
            rules={[{ required: true, message: '请输入姓名/名称' }]}
          >
            <Input placeholder="请输入姓名/名称" />
          </Form.Item>

          <Form.Item label="性别" name="gender">
            <Select placeholder="请选择性别" allowClear>
              <Select.Option value="男">男</Select.Option>
              <Select.Option value="女">女</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="身份类型" name="identity_type">
            <Select placeholder="请选择身份类型" allowClear>
              <Select.Option value="身份证">身份证</Select.Option>
              <Select.Option value="统一社会信用代码">统一社会信用代码</Select.Option>
              <Select.Option value="护照">护照</Select.Option>
              <Select.Option value="其他">其他</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="身份证号/统一社会信用代码" name="identity_number">
            <Input placeholder="请输入身份证号/统一社会信用代码" />
          </Form.Item>

          <Form.Item label="联系电话" name="phone">
            <Input placeholder="请输入联系电话" />
          </Form.Item>

          <Form.Item label="地址" name="address">
            <TextArea rows={2} placeholder="请输入地址" />
          </Form.Item>

          <Form.Item label="法定代表人" name="legal_representative">
            <Input placeholder="请输入法定代表人（企业）" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CaseBasicInfoMobile;
